import React, { useCallback, useEffect, useRef } from 'react'
import {
  ChartOptionElement,
  useGetChartOptionQuery,
  useGetPrintReportElementsQuery,
  useGetReportDetailsQuery,
  useGetReportFavoriteElementsQuery,
} from 'gqlTypes'
import { Box, Typography } from '@mui/material'
import ReportElement from 'components/ReportElement/ReportElement'
import { useSetRecoilState } from 'recoil'
import { chartOptionAtom, keyForElementIdAndType } from 'recoil/settings'
import { first, pick } from 'lodash'

type Props = {
  reportId: string
  sectionId: string
  sectionTitle?: string | null
  favorites: boolean
  onLoadComplete?: VoidFunction
  confidential: boolean
}

const PdfReportSection: React.FC<Props> = ({
  reportId,
  sectionId,
  sectionTitle,
  favorites,
  onLoadComplete,
  confidential,
}) => {
  const loaded = useRef<boolean>(false)

  const setGlobalOptions = useSetRecoilState(
    chartOptionAtom(
      keyForElementIdAndType({
        id: reportId || '',
        type: ChartOptionElement.Report,
      })
    )
  )

  const { data: chartOptionData } = useGetChartOptionQuery({
    variables: { id: reportId || '', type: ChartOptionElement.Report },
  })

  const { data: reportData } = useGetReportDetailsQuery({
    variables: { id: reportId ?? '' },
  })
  const { data: elementData } = useGetPrintReportElementsQuery({
    variables: { reportId, sectionId },
  })
  const { data: favoriteData } = useGetReportFavoriteElementsQuery({
    variables: {
      reportId: reportId,
      sectionId: sectionId,
    },
  })
  const handleLoadComplete = useCallback(() => {
    if (loaded.current === false && onLoadComplete) {
      onLoadComplete()
      loaded.current = true
    }
  }, [onLoadComplete])
  const report = reportData?.report
  const reportElements = favorites
    ? favoriteData?.reportFavoriteElements || []
    : elementData?.printReportElements || []

  useEffect(() => {
    if (chartOptionData) {
      if (chartOptionData.chartOption) {
        setGlobalOptions(
          pick(chartOptionData.chartOption, [
            'cohort',
            'subgroup',
            'pastResults',
            'config',
          ])
        )
      } else {
        setGlobalOptions({
          cohort: first(reportData?.report?.cohorts),
          subgroup: first(reportData?.report?.subgroups),
          pastResults: true,
        })
      }
    }
  }, [chartOptionData, reportData])

  if (reportElements.length === 0) {
    handleLoadComplete()
    return null
  }

  return (
    <Box display="block" key={`print-section__${sectionId}`}>
      {sectionTitle && (
        <Box pb={4}>
          <Typography
            id={`print-section__${sectionId}`}
            variant="h2"
            sx={{ display: 'block', breakBefore: 'page', breakAfter: 'avoid' }}
          >
            {sectionTitle}
          </Typography>
        </Box>
      )}
      <Box>
        {confidential && (
          <Box className="report-confidential">confidential</Box>
        )}
        <Box
          className="report-footer"
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          {report && report.title && (
            <Box className="report-footer__title">{report.title}</Box>
          )}
          <Box className="report-footer__page_num"></Box>
        </Box>
        {report &&
          reportElements.map((element, i) => {
            if (i + 1 === reportElements.length && loaded.current === false) {
              setTimeout(() => handleLoadComplete(), 0)
            }
            return (
              <Box
                key={element.id}
                sx={{ display: 'block', width: '100%', overflow: 'hidden' }}
              >
                <ReportElement
                  reportId={report.id}
                  report={report}
                  element={element}
                  sectionId={sectionId}
                  onUpdateFavorites={() => null}
                  print
                />
              </Box>
            )
          })}
      </Box>
    </Box>
  )
}

export default PdfReportSection
